import type {
  BrandSpecials,
  MaterializedMenuProduct,
} from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { Flex, Typography } from '@iheartjane/reefer';
import type { LabeledOption } from '@iheartjane/types';
import { useMemo } from 'react';

import { DMMenuRow } from '@jane/bloom/components/menuRow/dmMenuRow';
import {
  FilterSelector,
  MobileFilterRow,
} from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { ProductGrid } from '@jane/bloom/components/productListingPage/productGrid';
import type { CustomRow } from '@jane/bloom/dataAccess/roots/storeApi/bloom/menu.server';
import type { CustomLanguage } from '@jane/bloom/loaders/rootLoader';
import { createCustomRowWeightFilter } from '@jane/bloom/utils/specials/shouldShowWeight';
import { formatProductCount } from '@jane/bloom/utils/strings/formatProductCount';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type { StoreSpecials } from '@jane/bloom/viewModels/schemas/storeSpecials';
import type { SortOptions } from '@jane/bloom/viewModels/sortOptions';

type CustomRowDetailsProps = {
  brandSpecials: BrandSpecials;
  currentSort: SortOptions;
  customLanguage: CustomLanguage;
  customRow: CustomRow;
  janeGold: JaneGold;
  menuTopRowInstance?: SmartSort<MaterializedMenuProduct>;
  pauseRefetching: () => void;
  preloadedProductIds: number[];
  rowInstance: SmartSort<MaterializedMenuProduct> | undefined;
  sortOptions: LabeledOption<SortOptions>[];
  specials: StoreSpecials;
  unpauseRefetching: () => void;
};

export const CustomRowDetails = ({
  brandSpecials,
  currentSort,
  customLanguage,
  customRow,
  janeGold,
  menuTopRowInstance,
  pauseRefetching,
  preloadedProductIds,
  rowInstance,
  sortOptions,
  specials,
  unpauseRefetching,
}: CustomRowDetailsProps) => {
  const hasResults = useMemo(
    () => (rowInstance?.products.length ?? 0) > 0,
    [rowInstance?.products.length]
  );

  const weightFilter = useMemo(
    () => createCustomRowWeightFilter(customRow.rules),
    [customRow.rules]
  );

  if (!rowInstance) {
    return <></>;
  }

  return (
    <>
      <div className="filters-container">
        <FilterSelector
          availableProductsCount={rowInstance.totalProducts}
          currentSort={currentSort}
          hideLabeledFilters
          sortOptions={sortOptions}
        />
      </div>

      <MobileFilterRow
        availableProductsCount={rowInstance.totalProducts}
        sortOptions={sortOptions}
        currentSort={currentSort}
      />

      <main className="main">
        <Typography
          color="inherit"
          data-testid="product-count"
          className="product-count"
        >
          {formatProductCount(rowInstance.totalProducts)}
        </Typography>

        {!hasResults && (
          <Flex justifyContent="center" alignItems="center" height="500px">
            <Typography
              color="inherit"
              variant="h1-semibold"
              data-testid="no-results"
            >
              No Products Available
            </Typography>
          </Flex>
        )}

        {menuTopRowInstance && (
          <DMMenuRow
            pauseRefetching={pauseRefetching}
            unpauseRefetching={unpauseRefetching}
            brandSpecials={brandSpecials}
            menuTopRow={menuTopRowInstance}
            customLanguage={customLanguage}
            specials={specials}
            janeGold={janeGold}
            embedInProductGrid={true}
          />
        )}

        <ProductGrid
          brandSpecials={brandSpecials}
          customLanguage={customLanguage}
          key={customRow.display_name}
          preloadedProductIds={preloadedProductIds}
          rowInstance={rowInstance}
          specials={specials}
          janeGold={janeGold}
          weightFilter={weightFilter}
        />
      </main>
    </>
  );
};
